import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { USER_STALE_TIME } from '../../config';
import type { ApiError } from '../../errors';
import { GeneratedUsersApi } from '../../GeneratedApi';
import type { CreateUserData, DisableUserData, ResetPasswordData, SendSignupEmailData, UpdateUserData } from '../../generated/users';
import { transformUserWithPermissions, User } from '../../models';
import { PAGINATED_USERS_QUERY_CACHE_KEY, USERS_BY_ID_QUERY_CACHE_KEY } from './useUsers';
export const useUser = t0 => {
  const $ = _c(15);
  const {
    userId,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  let t2;
  let t3;
  if ($[0] !== userId) {
    t2 = ["user", userId];
    t3 = () => userId ? GeneratedUsersApi.users.getUser({
      path: {
        user_id: userId
      }
    }) : undefined;
    $[0] = userId;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  const t4 = !!(userId && enabled);
  let t5;
  if ($[3] !== t2 || $[4] !== t3 || $[5] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4,
      staleTime: USER_STALE_TIME
    };
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = useQuery(t5);
  let data;
  let rest;
  if ($[7] !== t6) {
    ({
      data,
      ...rest
    } = t6);
    $[7] = t6;
    $[8] = data;
    $[9] = rest;
  } else {
    data = $[8];
    rest = $[9];
  }
  let t7;
  if ($[10] !== data) {
    t7 = data ? new User(transformUserWithPermissions(data)) : undefined;
    $[10] = data;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const transformedUser = t7;
  let t8;
  if ($[12] !== rest || $[13] !== transformedUser) {
    t8 = {
      ...rest,
      data: transformedUser
    };
    $[12] = rest;
    $[13] = transformedUser;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8;
};
export const useUserInfo = t0 => {
  const $ = _c(11);
  const {
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["userInfo"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== enabled) {
    t3 = {
      queryKey: t2,
      queryFn: _temp,
      enabled,
      staleTime: USER_STALE_TIME
    };
    $[1] = enabled;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = useQuery(t3);
  let data;
  let rest;
  if ($[3] !== t4) {
    ({
      data,
      ...rest
    } = t4);
    $[3] = t4;
    $[4] = data;
    $[5] = rest;
  } else {
    data = $[4];
    rest = $[5];
  }
  let t5;
  if ($[6] !== data) {
    t5 = data ? new User(transformUserWithPermissions(data)) : undefined;
    $[6] = data;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const transformedUserInfo = t5;
  let t6;
  if ($[8] !== rest || $[9] !== transformedUserInfo) {
    t6 = {
      ...rest,
      data: transformedUserInfo
    };
    $[8] = rest;
    $[9] = transformedUserInfo;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
};
export const useUpdateUser = () => {
  const $ = _c(5);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp2,
      onSuccess: t1 => {
        const {
          user_id: userId
        } = t1;
        queryClient.refetchQueries({
          queryKey: ["getUser", userId],
          type: "active",
          exact: true
        });
        queryClient.refetchQueries({
          queryKey: ["userInfo"],
          type: "active",
          exact: true
        });
        queryClient.invalidateQueries({
          queryKey: [PAGINATED_USERS_QUERY_CACHE_KEY]
        });
        queryClient.invalidateQueries({
          queryKey: [USERS_BY_ID_QUERY_CACHE_KEY]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    mutate: updateUser,
    isPending: isUpdateUserPending
  } = useMutation(t0);
  let t1;
  if ($[2] !== isUpdateUserPending || $[3] !== updateUser) {
    t1 = {
      updateUser,
      isUpdateUserPending
    };
    $[2] = isUpdateUserPending;
    $[3] = updateUser;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
export const useCreateUser = t0 => {
  const $ = _c(6);
  const {
    onSuccess
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== onSuccess || $[1] !== queryClient) {
    t1 = {
      mutationFn: _temp3,
      onSuccess: t2 => {
        const {
          user_id: userId
        } = t2;
        onSuccess?.(userId);
        queryClient.invalidateQueries({
          queryKey: [PAGINATED_USERS_QUERY_CACHE_KEY]
        });
        queryClient.invalidateQueries({
          queryKey: [USERS_BY_ID_QUERY_CACHE_KEY]
        });
      }
    };
    $[0] = onSuccess;
    $[1] = queryClient;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    mutate: createUser,
    isPending: isCreateUserPending
  } = useMutation(t1);
  let t2;
  if ($[3] !== createUser || $[4] !== isCreateUserPending) {
    t2 = {
      createUser,
      isCreateUserPending
    };
    $[3] = createUser;
    $[4] = isCreateUserPending;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
export const useDeactivateUser = t0 => {
  const $ = _c(6);
  const {
    onSuccess
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== onSuccess || $[1] !== queryClient) {
    t1 = {
      mutationFn: _temp4,
      onSuccess: () => {
        onSuccess?.();
        queryClient.invalidateQueries({
          queryKey: [PAGINATED_USERS_QUERY_CACHE_KEY]
        });
        queryClient.invalidateQueries({
          queryKey: [USERS_BY_ID_QUERY_CACHE_KEY]
        });
      }
    };
    $[0] = onSuccess;
    $[1] = queryClient;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    mutate: deactivateUser,
    isPending: isDeactivatingUser
  } = useMutation(t1);
  let t2;
  if ($[3] !== deactivateUser || $[4] !== isDeactivatingUser) {
    t2 = {
      deactivateUser,
      isDeactivatingUser
    };
    $[3] = deactivateUser;
    $[4] = isDeactivatingUser;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
export const useSendSignupEmail = t0 => {
  const $ = _c(10);
  const {
    onSuccess,
    onError
  } = t0;
  let t1;
  if ($[0] !== onSuccess) {
    t1 = (_, t2) => {
      const {
        email
      } = t2;
      return onSuccess?.(email);
    };
    $[0] = onSuccess;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onError) {
    t2 = err => onError?.(err);
    $[2] = onError;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = {
      mutationFn: _temp5,
      onSuccess: t1,
      onError: t2
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    mutate: sendSignupEmail,
    isPending: isSendingSignupEmail
  } = useMutation(t3);
  let t4;
  if ($[7] !== isSendingSignupEmail || $[8] !== sendSignupEmail) {
    t4 = {
      sendSignupEmail,
      isSendingSignupEmail
    };
    $[7] = isSendingSignupEmail;
    $[8] = sendSignupEmail;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
export const useSendPasswordResetEmail = t0 => {
  const $ = _c(10);
  const {
    onSuccess,
    onError
  } = t0;
  let t1;
  if ($[0] !== onSuccess) {
    t1 = (_, t2) => {
      const {
        email
      } = t2;
      return onSuccess?.(email);
    };
    $[0] = onSuccess;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onError) {
    t2 = err => onError?.(err);
    $[2] = onError;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = {
      mutationFn: _temp6,
      onSuccess: t1,
      onError: t2
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    mutate: sendPasswordResetEmail,
    isPending: isSendingPasswordResetEmail
  } = useMutation(t3);
  let t4;
  if ($[7] !== isSendingPasswordResetEmail || $[8] !== sendPasswordResetEmail) {
    t4 = {
      sendPasswordResetEmail,
      isSendingPasswordResetEmail
    };
    $[7] = isSendingPasswordResetEmail;
    $[8] = sendPasswordResetEmail;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
function _temp() {
  return GeneratedUsersApi.users.getUserInfo();
}
function _temp2(data) {
  return GeneratedUsersApi.users.updateUser(data);
}
function _temp3(data) {
  return GeneratedUsersApi.users.createUser(data);
}
function _temp4(data) {
  return GeneratedUsersApi.users.disableUser(data);
}
function _temp5(query) {
  return GeneratedUsersApi.users.sendSignupEmail({
    query
  });
}
function _temp6(query) {
  return GeneratedUsersApi.users.resetPassword({
    query
  });
}